/**
 * shared/image.ts —— 上传前的图片压缩（2026-08-22，性能优化）
 *
 * 🔴 **为什么要有这一层**：手机直出照片普遍 3—5MB / 长边 4000px，而**全站没有任何一处显示原图**——
 *   打卡卡片封面最大 355×230 CSS px（`12_checkins.css` 的 `.ck-card` 高 230、三列网格），
 *   详情弹窗缩略图 88×88。3MB 下来只为画一个 355px 宽的格子，**99% 的字节是白下的**。
 *   一页 52 张卡就是 150MB+ 的下行，打卡页因此慢到不能用。
 *
 * 所以在**上传前**就把长边压到 2048、转 JPEG q0.88：3MB → 约 400—700KB（降 ~80%），
 * 上行、存储、下行**三头一起省**。
 *
 * 🔴 **2026-09-09 由 1600/q0.82 调到 2048/q0.88**：「点击看大图」已经真的在用了
 *   （`checkins.ts` 的 `lightbox()` 走 `o` 变体 = 桶里那张原样的图），全屏铺开时
 *   1600 在 2× 屏上不够清楚。2048 覆盖 1440p 全屏与多数笔记本 2× 的看图场景；
 *   卡片 720、缩略 176 都是服务端现缩的，**这次调大不影响列表页的下行**，
 *   只有点开大图那一张变大。
 * ⚠ **不要为了更清楚继续往上调**：再大就该做"缩略图 + 原图"双份方案
 *   （给 `activity_photo` 加列），而不是把每个人的看图流量一起抬上去。
 *
 * ⚠ **原图不保留**（2026-08-22 拍板）：打卡照片的用途只有看板内展示。
 *
 * ⚠ **压缩失败一律回退原图，不抛异常**：HEIC 在 Chrome 里解不开、超大图 canvas 分配失败、
 *   老浏览器没有 `createImageBitmap`——这些都不该让用户传不上图。慢总比传不上强。
 *
 * 📊 **2026-08-22 实测（4032×3024 噪点图，最坏情况）**，决定了下面两个设计：
 *   | `createImageBitmap` 解码 49ms | `drawImage` **0ms** | `toBlob` 编码 1037ms |
 *   三段**主线程被堵均为 0ms**——解码与编码本来就在主线程之外。
 *   ① 所以**不用 Web Worker + OffscreenCanvas**：主线程压根没被堵，上 Worker 是净增复杂度。
 *   ② 所以**可以并发**：瓶颈在 off-thread 编码，串行 6 张 6.6s → 并发 3 只要 2.3s（提速 65%）。
 *
 * ⚠ **EXIF 方向必须处理**：iPhone 竖拍照片的像素其实是横的，靠 EXIF 的 orientation 标记转正。
 *   画进 canvas 会丢掉这个标记 → 存进去的图**躺倒 90°**，而且**不会报任何错**。
 *   靠 `createImageBitmap(file, { imageOrientation: 'from-image' })` 兜住；
 *   `<img>` 兜底路径里靠 CSS 的 `image-orientation: from-image`（现代浏览器默认就是它）。
 */

/** 长边上限。⚠ 改这个值等于改画质与体积的平衡点，改前先看文件头那两条 ⚠ */
export const MAX_EDGE = 2048;
/** JPEG 质量。0.88 给「点击看大图」留余量；0.82 全屏看时人脸、文字边缘能看出块 */
export const QUALITY = 0.88;
/**
 * 小于这个体积直接放过：已经很小的图再走一遍解码 + 编码，**可能反而变大**，
 * 还白白花掉几百毫秒。⚠ 判据是**体积不是尺寸**——体积才是慢的直接原因。
 */
export const SKIP_BYTES = 300 * 1024;

/**
 * 算目标尺寸（纯函数，可单测）。长边已经不超上限时返回 `scaled:false`，
 * 调用方据此**跳过缩放但仍然转码**——4000px 的 PNG 截图尺寸不算大也能有 8MB。
 * ⚠ 结果向下取整且至少为 1：canvas 宽高为 0 会直接抛 `IndexSizeError`。
 */
export function planResize(
  w: number,
  h: number,
  maxEdge: number = MAX_EDGE,
): { w: number; h: number; scaled: boolean } {
  if (!(w > 0) || !(h > 0)) return { w: 1, h: 1, scaled: false };
  const long = Math.max(w, h);
  if (long <= maxEdge) return { w: Math.round(w), h: Math.round(h), scaled: false };
  const k = maxEdge / long;
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)), scaled: true };
}

/** 换成 .jpg 扩展名（压缩后一律是 JPEG，扩展名要跟着变，否则签发的对象键会写成 .heic） */
function toJpgName(name: string): string {
  return name.replace(/\.[^.]*$/, '') + '.jpg';
}

/** 解码成位图。⚠ `imageOrientation` 是 EXIF 转正的关键，见文件头 */
async function decode(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file, { imageOrientation: 'from-image' });
  }
  // 兜底：老内核没有 createImageBitmap。⚠ blob URL 必须显式 revoke，否则整页留着这张大图
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('图片解码失败'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function sizeOf(src: ImageBitmap | HTMLImageElement): { w: number; h: number } {
  return src instanceof HTMLImageElement
    ? { w: src.naturalWidth, h: src.naturalHeight }
    : { w: src.width, h: src.height };
}

/**
 * 压缩一张图。**失败回退原文件**，永不抛。
 *
 * ⚠ 压出来比原文件还大时也回退原图——已经优化过的小 JPEG 重编码经常是这个结果。
 */
export async function compressImage(file: File, maxEdge: number = MAX_EDGE): Promise<File> {
  if (!file.type.startsWith('image/') || file.size <= SKIP_BYTES) return file;

  let src: ImageBitmap | HTMLImageElement | null = null;
  const release = (): void => {
    if (src && !(src instanceof HTMLImageElement)) src.close();
    src = null;
  };
  try {
    src = await decode(file);
    const { w, h } = sizeOf(src);
    const target = planResize(w, h, maxEdge);

    const canvas = document.createElement('canvas');
    canvas.width = target.w;
    canvas.height = target.h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    // 缩图靠浏览器的双线性采样即可；`imageSmoothingQuality` 在 Safari 上对大比例缩放差别明显
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(src, 0, 0, target.w, target.h);

    /* 🔴 **画完立刻放掉源位图，不要等到 finally**。
       一张 4032×3024 的位图是 4032×3024×4 ≈ 48MB；`toBlob` 那一步要跑 1 秒，
       放在 finally 里等于这 48MB 白挂 1 秒。并发 3 时峰值从 ~146MB 降到 ~23MB
       （只剩三张 1600×1200 的画布），手机上是能不能活下来的差别。 */
    release();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', QUALITY),
    );
    canvas.width = canvas.height = 0;
    if (!blob || blob.size >= file.size) return file;

    return new File([blob], toJpgName(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch (e) {
    // ⚠ 只记不抛：HEIC 解不开 / canvas 分配失败都走这里，用户照样能把原图传上去
    console.warn(`[image] 压缩失败，改传原图：${file.name}`, e);
    return file;
  } finally {
    release();
  }
}

/**
 * 并发上限。瓶颈是 off-thread 的 JPEG 编码，**不是主线程**，所以串行纯属浪费
 * （实测 6 张：串行 6.6s，并发 3 只要 2.3s）。
 * ⚠ 不要为了更快调到 6+：同时在飞的画布越多，手机上越接近被系统回收。3 是速度与内存的折中。
 */
export const CONCURRENCY = 3;

/**
 * 有并发上限的 map，**保序**（纯函数，可单测）。
 *
 * 🔴 **保序是这个函数存在的全部理由**：并发下完成顺序与入参顺序不一致，
 *   若按完成先后往数组里 `push`，照片就会**张冠李戴**——对象键 i 配上了文件 j 的内容，
 *   而且**不报任何错**，只是封面莫名其妙变成了另一张。所以一律按下标写回。
 * ⚠ `onDone` 报的是**完成计数**，不是第几个：按序号报会出现「3/6」之后又跳回「2/6」。
 */
export async function mapLimit<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
  onDone?: (done: number, total: number) => void,
): Promise<R[]> {
  const out = new Array<R>(items.length);
  let next = 0;
  let done = 0;
  const lane = async (): Promise<void> => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i] as T, i); // ⚠ 按下标写回，不是 push
      done += 1;
      onDone?.(done, items.length);
    }
  };
  // ⚠ 车道数要夹住：空数组时 Math.min 得 0，Promise.all([]) 直接过，正是想要的
  await Promise.all(Array.from({ length: Math.max(0, Math.min(limit, items.length)) }, lane));
  return out;
}

/**
 * 压一批（**并发 3，保序**）。`onProgress(done, total)` 报的是完成张数。
 * ⚠ 返回数组顺序**严格与入参一致**——对象键与预览图靠这个顺序对齐。
 */
export function compressAll(
  files: File[],
  onProgress?: (done: number, total: number) => void,
): Promise<File[]> {
  return mapLimit(files, CONCURRENCY, (f) => compressImage(f), onProgress);
}
